import fetcher from "./fetch";

export interface BlobWithFilename {
  blob: Blob;
  filename: string | null;
}

/**
 * Extract filename from a presigned URL path
 * Example: https://d2h23sytqthqav.cloudfront.net/.../Brooks-Running-Report.csv?... → "Brooks-Running-Report.csv"
 */
export function extractFilenameFromUrl(url: string): string | null {
  try {
    const pathname = new URL(url).pathname;
    const filename = pathname.split("/").pop();
    if (filename && filename.includes(".")) {
      return filename;
    }
    return null;
  } catch {
    return null;
  }
}

async function getPresignedUrl(url: string, params?: URLSearchParams): Promise<string> {
  const urlWithParams = params ? `${url}?${params.toString()}` : url;

  const response = await fetcher<{ url?: string; download_url?: string } | string>(urlWithParams);
  if (!response) {
    const errorMessage = "Failed to download file: could not get download URL";
    console.error(errorMessage);
    throw new Error(errorMessage);
  }

  if (typeof response === "string") {
    return response;
  }
  const urlFromResponse = "url" in response ? response.url : response.download_url;
  if (urlFromResponse == null) {
    throw new Error("Failed to download file: no URL in response");
  }
  return urlFromResponse;
}

export interface DownloadProgressOptions {
  onProgress?: (progress: number) => void;
}

export async function fetchBlobThroughDownloadApi(
  url: string,
  params?: URLSearchParams,
  options?: DownloadProgressOptions
): Promise<Blob> {
  const presignedUrl = await getPresignedUrl(url, params);
  return await fetchBlobFromPresignedUrl(presignedUrl, options);
}

/**
 * Fetch blob with filename extracted from the presigned URL path.
 * Use this when you need the server-provided filename (e.g., CSV exports).
 */
export async function fetchBlobWithFilename(
  url: string,
  params?: URLSearchParams,
  options?: DownloadProgressOptions
): Promise<BlobWithFilename> {
  const presignedUrl = await getPresignedUrl(url, params);
  const blob = await fetchBlobFromPresignedUrl(presignedUrl, options);
  const filename = extractFilenameFromUrl(presignedUrl);
  return { blob, filename };
}

export async function fetchBlobFromPresignedUrl(
  presignedUrl: string,
  options?: DownloadProgressOptions
): Promise<Blob> {
  const response = await fetch(presignedUrl);

  if (!response || !response.ok) {
    const errorMessage = `Failed to download file: request failed${response ? ` (${response.status})` : ""}`;
    console.error(errorMessage);
    throw new Error(errorMessage);
  }

  if (options?.onProgress && response.body) {
    const contentLength = response.headers.get("Content-Length");
    const total = contentLength ? Number.parseInt(contentLength, 10) : Number.NaN;
    if (total > 0) {
      const reader = response.body.getReader();
      const chunks: BlobPart[] = [];
      let received = 0;

      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          received += value.length;
          options.onProgress(Math.round((received / total) * 100));
        }
      } finally {
        reader.releaseLock();
      }

      const blob = new Blob(chunks, {
        type: response.headers.get("Content-Type") || undefined
      });
      return blob;
    }
  }

  return response.blob();
}
